import type { GameModule } from './contracts'

/**
 * Адаптивная сложность.
 *
 * Держим игрока в зоне оптимальной трудности: целевая точность 0.75-0.85.
 * Выше - скучно, ниже - демотивирует.
 * См. docs/form-index-math.md, раздел 6.
 *
 * Уровень НЕ добавляет множитель к очкам: он уже влияет на сырой
 * показатель (на высоком уровне тот же span даёт более высокий z).
 * Множитель был бы двойным учётом.
 */

export const RAISE_ABOVE = 0.85
export const LOWER_BELOW = 0.6
/** Сколько раундов продержаться на уровне перед повышением */
export const ROUNDS_BEFORE_RAISE = 2
/**
 * Безошибочный раунд поднимает уровень сразу, без второго подтверждения.
 *
 * Без этого «быстрого коридора» подъём отстаёт от того, как быстро
 * игрок осваивается: за заход из 5 раундов можно было вырасти максимум
 * на 2 уровня, и сильный игрок весь заход проводил ниже своего потолка.
 * Правило двух раундов остаётся для «хорошо, но с помаркой» - там
 * подтверждение осмысленно, при 100% сомнений нет.
 */
export const PERFECT = 0.999

export interface DifficultyState {
  readonly level: number
  readonly roundsAtLevel: number
}

export type LevelChange = 'up' | 'down' | 'same'

export interface DifficultyStep {
  readonly state: DifficultyState
  readonly change: LevelChange
}

export function initialDifficulty(game: GameModule): DifficultyState {
  return { level: game.difficulty.initial, roundsAtLevel: 0 }
}

export function nextDifficulty(
  state: DifficultyState,
  accuracy: number,
  bounds: { min: number; max: number },
): DifficultyStep {
  const atLevel = state.roundsAtLevel + 1

  if (accuracy >= PERFECT || (accuracy > RAISE_ABOVE && atLevel >= ROUNDS_BEFORE_RAISE)) {
    const level = Math.min(state.level + 1, bounds.max)
    return {
      state: { level, roundsAtLevel: 0 },
      change: level > state.level ? 'up' : 'same',
    }
  }

  if (accuracy < LOWER_BELOW) {
    const level = Math.max(state.level - 1, bounds.min)
    return {
      state: { level, roundsAtLevel: 0 },
      change: level < state.level ? 'down' : 'same',
    }
  }

  return { state: { level: state.level, roundsAtLevel: atLevel }, change: 'same' }
}
